import React, { useRef, useEffect, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

interface DateRangePickerProps {
	isOpen: boolean;
	onClose: () => void;
	selectedRange: { from: Date | null; to: Date | null };
	onSelect: (range: { from: Date | null; to: Date | null }) => void;
	primaryColor?: string;
}

const WEEKDAYS = ['ПН', 'ВТ', 'СР', 'ЧТ', 'ПТ', 'СБ', 'ВС'];
const MONTHS = [
	'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
	'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const hexToRgba = (hex: string, alpha: number): string => {
	const r = parseInt(hex.slice(1, 3), 16);
	const g = parseInt(hex.slice(3, 5), 16);
	const b = parseInt(hex.slice(5, 7), 16);
	return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const DateRangePicker: React.FC<DateRangePickerProps> = ({
	isOpen,
	onClose,
	selectedRange,
	onSelect,
	primaryColor = '#F54900'
}) => {
	const calendarRef = useRef<HTMLDivElement>(null);
	const [currentMonth, setCurrentMonth] = useState(new Date());
	const [tempFrom, setTempFrom] = useState<Date | null>(selectedRange.from);
	const [tempTo, setTempTo] = useState<Date | null>(selectedRange.to);

	useEffect(() => {
		if (isOpen) {
			setTempFrom(selectedRange.from);
			setTempTo(selectedRange.to);
			if (selectedRange.from) {
				setCurrentMonth(new Date(selectedRange.from));
			}
		}
	}, [isOpen, selectedRange.from, selectedRange.to]);

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
				onClose();
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen, onClose]);

	if (!isOpen) return null;

	const getDaysInMonth = (date: Date) => {
		const year = date.getFullYear();
		const month = date.getMonth();
		const firstDay = new Date(year, month, 1);
		const lastDay = new Date(year, month + 1, 0);
		const daysInMonth = lastDay.getDate();
		
		let startDay = firstDay.getDay() - 1;
		if (startDay < 0) startDay = 6;

		const days: (Date | null)[] = [];
		
		for (let i = 0; i < startDay; i++) {
			const prevDate = new Date(year, month, -startDay + i + 1);
			days.push(prevDate);
		}

		for (let i = 1; i <= daysInMonth; i++) {
			days.push(new Date(year, month, i));
		}

		const remainingDays = 42 - days.length;
		for (let i = 1; i <= remainingDays; i++) {
			days.push(new Date(year, month + 1, i));
		}

		return days;
	};

	const isSameDay = (d1: Date | null, d2: Date | null) => {
		if (!d1 || !d2) return false;
		return d1.toDateString() === d2.toDateString();
	};

	const isInRange = (date: Date) => {
		if (!tempFrom || !tempTo) return false;
		return date > tempFrom && date < tempTo;
	};

	const isCurrentMonth = (date: Date) => {
		return date.getMonth() === currentMonth.getMonth();
	};

	const isToday = (date: Date) => {
		return date.toDateString() === new Date().toDateString();
	};

	const handleDayClick = (date: Date) => {
		if (!tempFrom || (tempFrom && tempTo)) {
			setTempFrom(date);
			setTempTo(null);
		} else {
			if (date < tempFrom) {
				setTempTo(tempFrom);
				setTempFrom(date);
				onSelect({ from: date, to: tempFrom });
				onClose();
			} else {
				setTempTo(date);
				onSelect({ from: tempFrom, to: date });
				onClose();
			}
		}
	};

	const prevMonth = () => {
		setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1));
	};

	const nextMonth = () => {
		setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1));
	};

	const days = getDaysInMonth(currentMonth);

	const getDayStyle = (date: Date): React.CSSProperties => {
		const isStart = isSameDay(date, tempFrom);
		const isEnd = isSameDay(date, tempTo);
		const inRange = isInRange(date);
		const isOutside = !isCurrentMonth(date);
		const today = isToday(date);

		if (isStart || isEnd) {
			return {
				backgroundColor: primaryColor,
				color: 'white'
			};
		}
		if (inRange) {
			return {
				backgroundColor: hexToRgba(primaryColor, 0.15),
				color: '#0F172B'
			};
		}
		if (today && !isOutside) {
			return {
				backgroundColor: hexToRgba(primaryColor, 0.1),
				color: primaryColor
			};
		}
		return {};
	};

	const getDayClasses = (date: Date) => {
		const isStart = isSameDay(date, tempFrom);
		const isEnd = isSameDay(date, tempTo);
		const inRange = isInRange(date);
		const isOutside = !isCurrentMonth(date);
		const today = isToday(date);

		let classes = 'h-9 w-9 flex items-center justify-center text-sm cursor-pointer transition-colors ';

		if (isStart && isEnd) {
			classes += 'rounded-lg font-medium';
		} else if (isStart) {
			classes += 'rounded-l-lg font-medium';
		} else if (isEnd) {
			classes += 'rounded-r-lg font-medium';
		} else if (inRange) {
			classes += '';
		} else if (today && !isOutside) {
			classes += 'font-semibold rounded-lg';
		} else if (isOutside) {
			classes += 'text-[#CAD5E2] hover:bg-[#F1F5F9] rounded-lg';
		} else {
			classes += 'text-[#0F172B] hover:bg-[#F1F5F9] rounded-lg';
		}

		return classes;
	};

	return (
		<div
			ref={calendarRef}
			className="absolute top-full left-0 mt-2 bg-white rounded-[14px] border border-[#E2E8F0] shadow-lg z-50 p-4 select-none"
		>
			<div className="flex items-center justify-between mb-4">
				<button
					type="button"
					onClick={prevMonth}
					className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-[#F1F5F9] text-[#62748E] hover:text-[#0F172B] transition-colors"
				>
					<ChevronLeftIcon className="w-5 h-5" />
				</button>
				<span className="text-sm font-semibold text-[#0F172B] capitalize">
					{MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
				</span>
				<button
					type="button"
					onClick={nextMonth}
					className="h-8 w-8 flex items-center justify-center rounded-lg hover:bg-[#F1F5F9] text-[#62748E] hover:text-[#0F172B] transition-colors"
				>
					<ChevronRightIcon className="w-5 h-5" />
				</button>
			</div>

			<div className="grid grid-cols-7 gap-0 mb-2">
				{WEEKDAYS.map(day => (
					<div key={day} className="h-9 w-9 flex items-center justify-center text-xs font-medium text-[#62748E] uppercase">
						{day}
					</div>
				))}
			</div>

			<div className="grid grid-cols-7 gap-0">
				{days.map((date, idx) => (
					<div
						key={idx}
						onClick={() => date && handleDayClick(date)}
						className={date ? getDayClasses(date) : 'h-9 w-9'}
						style={date ? getDayStyle(date) : undefined}
					>
						{date?.getDate()}
					</div>
				))}
			</div>

			{(tempFrom || tempTo) && (
				<div className="mt-3 pt-3 border-t border-[#E2E8F0] flex justify-end">
					<button
						type="button"
						onClick={() => {
							setTempFrom(null);
							setTempTo(null);
							onSelect({ from: null, to: null });
							onClose();
						}}
						className="text-sm font-medium text-[#62748E] hover:text-[#0F172B] px-3 py-1.5 rounded-lg hover:bg-[#F1F5F9] transition-colors"
					>
						Сбросить
					</button>
				</div>
			)}
		</div>
	);
};

export default DateRangePicker;
